/**
 * Consultant profile notice on the cart (Dieselbrook/annique-issues#308).
 *
 * Reveals snippets/consultant-profile-cart-notice.liquid only for a signed-in consultant whose
 * profile is incomplete, per DBM's own verdict:
 *
 *   GET /apps/consultant/api/extension/profile
 *       -> { profile_complete: bool, missing_profile_fields: string[], ... }
 *
 * ⛔ The decision itself is NOT here. `cartNotice` lives in assets/consultant-profile-wizard.js
 * and is shared with the personal-info section, so the storefront cannot end up with two answers
 * to "is the gate on". This file is the plumbing: fetch, apply, re-apply.
 *
 * CACHE-SAFE (mirrors bmtm-cart-banner beside it): the notice ships HIDDEN for every visitor — no
 * Liquid `customer` read — so the cached cart HTML is identical for everyone, and this script
 * reveals it client-side. GRACEFUL DEGRADE: signed out, not a consultant, an unreadable response,
 * or any error at all leaves it hidden. Saying nothing is always the safe outcome here; the gate
 * is a nudge, not a mechanism, and the mechanism (if it is ever armed) lives in checkout.
 *
 * The cart drawer re-renders its innerHTML on every cart change (assets/theme.js CartDrawer),
 * recreating the notice hidden — so re-apply the last known verdict on cart-drawer:refreshed /
 * cart:refresh, exactly as the BMTM banner does.
 */
import {
  NOTICE_NONE,
  NOTICE_COPY,
  cartNotice,
} from './consultant-profile-wizard.js';

(function () {
  'use strict';
  if (window.__caProfileCartNoticeInit) { return; } // one init even if two sections load us
  window.__caProfileCartNoticeInit = true;

  var SELECTOR = '#ca-profile-cart-notice, .ca-profile-cart-notice';
  var tone = NOTICE_NONE;

  function hosts() { return document.querySelectorAll(SELECTOR); }

  function apply() {
    var els = hosts();
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var copy = el.querySelector('#ca-profile-cart-notice-copy, .ca-profile-cart-notice__text span');
      var link = el.querySelector('#ca-profile-cart-notice-link, .ca-profile-cart-notice__link');
      if (tone === NOTICE_NONE) {
        el.hidden = true;
        continue;
      }
      if (copy) { copy.textContent = NOTICE_COPY[tone] || ''; }
      // The link only ever points at the wizard; the tone changes the SENTENCE, not the
      // destination. She is going to the same page either way.
      if (link) {
        var target = el.getAttribute('data-profile-page');
        if (target) { link.setAttribute('href', target); }
      }
      el.hidden = false;
    }
  }

  function gateEnabled() {
    var el = document.querySelector(SELECTOR);
    // Absent attribute and empty attribute are the SAME thing — a switch nobody has written. The
    // shared isGateArmed treats both as disarmed; this only needs to hand it a string.
    return el ? (el.getAttribute('data-gate-enabled') || '') : '';
  }

  function profileUrl() {
    var el = document.querySelector(SELECTOR);
    return (el && el.getAttribute('data-profile-url')) || '/apps/consultant/api/extension/profile';
  }

  function decide(payload) {
    // profile_complete absent (an older API, or a body we could not read) stays undefined, which
    // cartNotice answers with "none". Never guess "incomplete" from a missing field.
    var complete = payload && typeof payload.profile_complete === 'boolean'
      ? payload.profile_complete
      : undefined;
    tone = cartNotice({ profileComplete: complete, gateEnabled: gateEnabled() });
    apply();
  }

  function boot() {
    if (!document.querySelector(SELECTOR)) { return; }

    fetch(profileUrl(), { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        // 401 = signed out, 403 = not a consultant. Both are ordinary, not errors, and both mean
        // this notice has nothing to say.
        if (!r.ok) { return null; }
        return r.json();
      })
      .then(function (body) {
        // The App-Proxy BFF answers { ok, data } on some routes and a bare object on others.
        decide(body && body.data ? body.data : body);
      })
      .catch(function () { /* stays hidden */ });

    // The drawer rebuilds its DOM on every cart change; re-assert what we already know rather than
    // re-fetching, so a burst of quantity edits costs one request.
    document.addEventListener('cart-drawer:refreshed', apply);
    document.addEventListener('cart:refresh', apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
